// Top-level chat surface.
//
// Three responsibilities:
//   1. AuthGate — probes GET /me; renders <AuthScreen /> on 401 (a retry view on any other failure), <Chat />
//      on 200. dsh_session is HttpOnly so the SPA cannot read it directly;
//      /me is the only signal of session validity.
//   2. Chat shell — header (56px, wordmark RIGHT + status pill LEFT),
//      message stream (RTL bubble alignment per design-system §4),
//      composer (plural-neutral copy, character counter ≥9k, max 10k).
//   3. SSE consumer — reader-loop over /chat response.body, parsing
//      `data:` envelopes, `data: [DONE]`, `event: timeout`, `event: error`.
//
// thread_id is generated once with crypto.randomUUID() per user and
// persisted to localStorage[dsh_thread_${userId}]. One user = one
// continuous web thread (api-contract.md §POST /chat).
//
// Error states (4.1): network errors render inline per design-system
// §4 (bubble `שגיאת רשת` + `נסו שוב`) AND drive the top `NetworkBanner`
// (`connected`, also wired to navigator online/offline). A 401 from any
// authenticated request or the live stream surfaces the `SessionExpiredOverlay`
// (re-auth), preserving the unsent draft in localStorage — under a key naming
// the identity that typed it, so a shared browser never hands it to whoever
// signs in next (identityKeys.ts) — so it rehydrates after re-login. An
// in-flight reply that never lands is resolved by a client-side
// watchdog (REPLY_TIMEOUT_MS, just past the backend detached ceiling) into a
// retryable timeout bubble.

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getOrgConfig } from './brand.ts';
import { AuthScreen } from './components/AuthScreen.tsx';
import {
  chatOutcome,
  historyOutcome,
  logoutOutcome,
  NETWORK_ERROR_TEXT,
  probeMe,
  readAnswer,
  type Failure,
  type Impersonating,
  type ProbedAuth,
} from './authProbe.ts';
import { CodeInput, isCompleteCode } from './components/CodeInput.tsx';
import { ComponentSwitch, formatTimestamp } from './components/ComponentSwitch.tsx';
import { GuestChat } from './components/GuestChat.tsx';
import { NetworkBanner } from './components/NetworkBanner.tsx';
import { OnboardingChat } from './components/OnboardingChat.tsx';
import { ShowMore } from './components/ShowMore.tsx';
import {
  claimIdentity,
  draftKeyFor,
  PENDING_R_CAPTURE_KEY,
  pendingRKeyFor,
  releaseIdentity,
} from './identityKeys.ts';
import { capturePendingQ, drainPendingQ } from './pendingQ.ts';
import { mediaOf, turnFromStream } from './streamTurn.ts';
import type { ChatMedia, ChatTurn, Component } from './protocol.ts';
import { chatBody, makeRecord, inflightJson, type SendRecord } from './sendRecord.ts';
import { acceptSuggestions } from './suggestions.ts';

// ---- Constants ------------------------------------------------------------

const MAX_MESSAGE_LEN = 10_000;
const COUNTER_THRESHOLD = 9_000;
const COUNTER_WARN_THRESHOLD = 9_500;
// /יציאה fires logout directly; the other commands populate the input
// and submit through the chat pipeline so the agent answers.
const SLASH_COMMANDS = [
  { command: '/עזרה', label: 'עזרה', kind: 'send' as const },
  { command: '/דוח', label: 'דוח שבועי', kind: 'send' as const },
];
// `?q=<text>` deep-link: capture once at module load (before React renders, so
// the param survives the magic-link auth redirect 302 → `/`) and drain it
// post-hydration to PREFILL the composer (: NO auto-send — the user taps
// send). `capturePendingQ`/`drainPendingQ` live in pendingQ.ts; the keys they
// write and read live in identityKeys.ts.
// `/r/<cid>` deep-link — the magic-link landing target for the
// chat-app PNG inline-button. Captured at module load under the unscoped
// capture key (no identity exists yet), persists across the auth redirect
// (same shape as the `?q=` stash), claimed into the signing-in identity's own
// key by `claimIdentity`, and drained from there post-hydration to scroll +
// highlight the matching bubble.
const R_PATH_RE = /^\/r\/([A-Za-z0-9_-]+)\/?$/;
// per-thread inflight scaffold. `send()` writes
// `{cid, text}` here BEFORE the POST so a crash mid-send leaves a
// recoverable record (industry idempotency-key best practice). Cleared
// on success or after a non-recoverable error.
const INFLIGHT_KEY_PREFIX = 'dsh_inflight_';
// 4.1 — client-side watchdog on an in-flight assistant reply. The
// backend kicks the agent loop DETACHED and publishes the reply (or a Hebrew
// failure) to /messages/stream; its own abort ceiling is DSH_DETACHED_CEILING_MS
// (default 5 min). This watchdog is the last-resort safety net for when NEITHER
// the reply NOR the failure ever lands (backend crash, a dropped stream whose
// replay also missed it): set just past the backend ceiling so a legitimately-
// slow report (~4 min observed) never false-times-out.
const REPLY_TIMEOUT_MS = 360_000;
// No 🙏 in an error state; it reads as the product pleading.
const REPLY_TIMEOUT_TEXT = 'התשובה מתעכבת מהרגיל. נסו שוב.';
capturePendingQ(window, localStorage, MAX_MESSAGE_LEN);
// `/r/<cid>` capture — mirrors the `?q=` pattern. If we land on
// `app.drishalom.com/r/<cid>` (Telegram inline-button tap, WAHA caption
// link tap), stash the cid and strip the path to `/` so the AuthGate
// redirect cycle doesn't trip on the unknown route. Chat's drain effect
// (post-hydration, single-fire) handles the scroll + highlight.
(() => {
  try {
    const m = window.location.pathname.match(R_PATH_RE);
    if (m && m[1]) {
      localStorage.setItem(PENDING_R_CAPTURE_KEY, m[1]);
      const url = '/' + (window.location.search ?? '') + (window.location.hash ?? '');
      window.history.replaceState(null, '', url);
    }
  } catch {
    /* SSR / disabled storage — ignore */
  }
})();

// ---- AuthGate -------------------------------------------------------------

type AuthState = { kind: 'checking' } | ProbedAuth;

const useAuth = (): { auth: AuthState; retry: () => void } => {
  const [state, setState] = useState<AuthState>({ kind: 'checking' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let mounted = true;
    void probeMe().then((next) => {
      if (mounted) setState(next);
    });
    return () => {
      mounted = false;
    };
  }, [attempt]);
  const retry = () => {
    setState({ kind: 'checking' });
    setAttempt((n) => n + 1);
  };
  return { auth: state, retry };
};

export const App = () => {
  const { auth, retry } = useAuth();
  // A sibling tab that enters or ends impersonation writes
  // `dsh_impersonation_changed` to localStorage; every other tab reloads so
  // no tab keeps acting under a stale identity (plan-review F5).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'dsh_impersonation_changed') window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  if (auth.kind === 'checking') return <CheckingFrame />;
  if (auth.kind === 'unauthenticated') return <AuthScreen />;
  if (auth.kind === 'unavailable') return <UnavailableFrame onRetry={retry} />;
  if (auth.kind === 'guest') return <GuestChat />;
  if (auth.kind === 'onboarding') return <OnboardingChat />;
  return (
    <Chat
      userId={auth.userId}
      canImpersonate={auth.canImpersonate}
      impersonating={auth.impersonating}
    />
  );
};

const UnavailableFrame = ({ onRetry }: { onRetry: () => void }) => (
  <div
    className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center"
    role="alert"
  >
    <p className="text-sm text-[var(--color-muted)]">אין חיבור לשרת כרגע</p>
    <button
      type="button"
      onClick={onRetry}
      className="cursor-pointer rounded-lg bg-[var(--color-primary)] px-6 py-3 text-sm font-bold text-white transition-colors hover:opacity-90"
    >
      נסו שוב
    </button>
  </div>
);

const CheckingFrame = () => (
  <div className="flex h-full items-center justify-center" aria-live="polite">
    <span className="inline-flex gap-1" aria-hidden="true">
      <span className="thinking-dot [animation-delay:0ms]" />
      <span className="thinking-dot [animation-delay:150ms]" />
      <span className="thinking-dot [animation-delay:300ms]" />
    </span>
  </div>
);

// ---- Chat shell -----------------------------------------------------------

// `id` is the canonical id (`client_msg_id` / `meta.cid`) sent with
// the POST and echoed by `/chat/history`, so the optimistic and hydrated
// versions of the same turn share one identifier and dedup is exact.
const userTurnFrom = (
  id: string,
  text: string,
  media?: ChatMedia,
): ChatTurn => ({
  role: 'user',
  id,
  text,
  createdAt: new Date().toISOString(),
  ...(media ? { media } : {}),
});

// An unexpected local failure after the answer was classified: retryable,
// not offline.
const LOCAL_FAILURE: Failure = {
  kind: 'error',
  text: NETWORK_ERROR_TEXT,
  offline: false,
  retry: true,
};

const errorTurn = (text: string): ChatTurn & { role: 'assistant' } => ({
  role: 'assistant',
  id: crypto.randomUUID(),
  text,
  components: null,
  event: null,
  createdAt: new Date().toISOString(),
});

// The dots-only assistant placeholder. `send()`/`sendAction()` insert
// one of these keyed by the reply `cid` returned in POST /chat's 202; the real
// reply (same `cid`) arrives on /messages/stream and `upsertById` replaces it
// in place. The loader IS the message slot — there is no separate bubble to
// remove, so the earlier stuck-spinner race cannot happen.
const pendingAssistantTurn = (id: string): ChatTurn => ({
  role: 'assistant',
  id,
  text: '',
  components: null,
  event: null,
  createdAt: new Date().toISOString(),
  pending: true,
});

// Single reducer for every live-incoming turn (the `/messages/stream`
// consumer is now the SOLE caller). One ordered channel, so no race — three
// rules:
//   1. replace-the-placeholder — if the incoming id matches a `pending`
//      assistant placeholder, swap it for the real content in place (dots →
//      answer). This is how the loader resolves; nothing is ever removed.
//   2. keep-on-dedup — if the incoming id matches a NON-pending turn (the
//      optimistic user bubble vs its own stream echo, or a reply arriving
//      twice), keep the existing one. Preserves the optimistic user bubble's
//      media and collapses duplicates to one bubble.
//   3. createdAt-sort — a replayed backfill or an interleaved cross-channel
//      turn lands in the right place, not at the tail.
const upsertById = (prev: ChatTurn[], incoming: ChatTurn): ChatTurn[] => {
  const existing = prev.find((t) => t.id === incoming.id);
  let next: ChatTurn[];
  if (existing) {
    const isPlaceholder =
      existing.role === 'assistant' &&
      'pending' in existing &&
      existing.pending === true;
    if (!isPlaceholder) return prev; // keep existing (dedup, preserve media)
    // resolve the placeholder in place
    next = prev.map((t) => (t.id === incoming.id ? incoming : t));
  } else {
    next = [...prev, incoming];
  }
  return next.sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
};

type DashboardPayload = {
  // `perms` is already on the wire (GET /dashboard returns the
  // full 8-flag object); the SPA just wasn't reading it. The empty-state
  // greeting needs `can_view_group` so it doesn't ask about a group the user
  // has no visibility into.
  user: {
    name: string;
    role_label: string | null;
    perms?: { can_view_group?: boolean };
  };
  suggestions: string[];
};

// History hydration envelope. Backend `GET /chat/history` returns
// chronological rows including persisted assistant components so refresh
// re-renders the original list / detail / chart / stat / confirm / result
// cards (hardening 2026-05-13). User media is re-signed for inline
// image bubbles.
type HistoryRow = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  media: { url: string; mime: string } | null;
  components: Component[] | null;
  createdAt: string;
};

const Chat = ({
  userId,
  canImpersonate,
  impersonating,
}: {
  userId: string;
  canImpersonate: boolean;
  impersonating: Impersonating | null;
}) => {
  // thread_id — persistent per user, generated client-side, reused across
  // sessions (api-contract.md §POST /chat).
  const [threadId] = useState(() => {
    const key = `dsh_thread_${userId}`;
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(key, fresh);
    return fresh;
  });

  const [turns, setTurns] = useState<ChatTurn[]>([]);
  // Gates the ?q= deep-link drain so it fires only AFTER hydration
  // settles (success / empty / !ok / network-fail). Without the gate the
  // drain races the hydration merge and the optimistic bubble can land
  // before history → out of order; with the gate it always lands after.
  const [hydrationDone, setHydrationDone] = useState(false);
  const [draft, setDraft] = useState(() => {
    // Runs before any other read of a per-identity key. Claims this browser for
    // `userId` — carrying the pre-identity deep-link captures over — or drops
    // what a different identity left behind (identityKeys.ts).
    claimIdentity(userId);
    return localStorage.getItem(draftKeyFor(userId)) ?? '';
  });
  // `pending` = "an assistant reply is in flight" — the composer stays
  // disabled from send until the reply (or a Hebrew error) lands. It is NOT
  // cleared when POST /chat returns 202 (the reply is still being composed
  // detached); it clears when the awaited reply arrives on /messages/stream
  // (or on a send-time network error). `awaitingReplyId` holds the reserved
  // reply cid so the stream consumer knows which incoming turn ends the wait.
  const [pending, setPending] = useState(false);
  const awaitingReplyId = useRef<string | null>(null);
  // 4.1 — watchdog timer for the in-flight reply (see REPLY_TIMEOUT_MS).
  const replyWatchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [connected, setConnected] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  // 4.1 — set when an authenticated request (or the live stream)
  // reports the session is no longer valid (401). Drives the re-auth overlay;
  // the draft is preserved in localStorage so the user's unsent text survives
  // the re-login.
  const [sessionExpired, setSessionExpired] = useState(false);
  // Staged attachment (uploaded to R2, awaiting send).
  // null until the user picks a file; cleared once the message lands.
  const [attachment, setAttachment] = useState<ChatMedia | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // Map from error-bubble id → { text, cid } that produced it:
  // retry must reuse the SAME cid as the original attempt, so the backend's
  // `idx_messages_org_channel_thread_external` dedup catches it as the same logical
  // user action and short-circuits via the `dedup_hit` SSE event instead of
  // creating a fresh inbound row + a fresh `respond()` call.
  const [retryMap, setRetryMap] = useState<Record<string, SendRecord>>({});
  // Error bubbles whose retry redoes a read or sign-out rather than a send:
  // 'history' re-reads /chat/history, 'logout' re-posts /auth/logout.
  const [redoMap, setRedoMap] = useState<Record<string, 'history' | 'logout'>>({});
  const [historyAttempt, setHistoryAttempt] = useState(0);
  // True only once /chat/history loaded — the /r/ report lookup waits for it.
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Latest error bubble id — drives the top NetworkBanner's connection-level
  // retry. Cleared on successful round-trip.
  const [lastErrorId, setLastErrorId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  // False until /dashboard says otherwise, so the group-flavoured
  // greeting is opt-in. A user without group visibility (the default new-user
  // grant is can_report + can_view_own) never sees it.
  const [canViewGroup, setCanViewGroup] = useState(false);

  // Bootstrap suggestion chips from /dashboard once after AuthGate clears.
  // Failures leave no buttons (D191) — the chat still works.
  useEffect(() => {
    let mounted = true;
    fetch('/dashboard', { credentials: 'include' })
      .then(async (res) => {
        if (!mounted || !res.ok) return;
        const data = (await res.json()) as DashboardPayload;
        setSuggestions(acceptSuggestions(data.suggestions));
        setCanViewGroup(data.user?.perms?.can_view_group === true);
      })
      .catch(() => {
        /* no buttons already in state */
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Every round-trip settles the connection banner through these two. A
  // failure adds its error bubble and makes it the banner's retry target; the
  // browser is offline only when the request never reached the server, so a
  // server's own failure answer clears the banner too. An answered round-trip
  // clears both, so no banner outlives its failure.
  const showFailure = (f: Failure): string => {
    const bubble = errorTurn(f.text);
    setTurns((t) => [...t, bubble]);
    setConnected(!f.offline);
    setLastErrorId(bubble.id);
    return bubble.id;
  };
  const answered = (): void => {
    setConnected(true);
    setLastErrorId(null);
  };

  // Hydrate the chat with prior turns on mount so a refresh
  // doesn't wipe the visible thread. A failed read is never shown as an empty
  // history: it gets an error bubble whose "נסו שוב" reads again
  // (historyOutcome); a 401 is the session-expired overlay.
  //
  // hardening 2026-05-13: persisted components on each assistant
  // row now replay alongside text so refresh re-renders the original
  // list / detail / chart / stat / confirm / result cards. Backend
  // sources them from `messages.meta.components`; rows older than the
  // schema change arrive with `components: null` and render text-only,
  // preserving the prior look without crashing.
  //
  // MERGE (not replace). Optimistic and hydrated turns share one canonical
  // id (server `meta.cid` ← client `client_msg_id` for inbound, ← SSE
  // envelope.id for outbound) so dedup is a trivial Set-based id check, then
  // sort by createdAt asc. `hydrationDone` flips on every settle path so the
  // ?q= prefill is never starved; `historyLoaded` only on a loaded history.
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const outcome = historyOutcome(
        await readAnswer(
          await fetch(
            `/chat/history?thread_id=${encodeURIComponent(threadId)}&limit=20`,
            { credentials: 'include' },
          ).catch((e: unknown) => e),
        ),
      );
      if (!mounted) return;
      setHydrationDone(true);
      if (outcome.kind === 'unauthorized') {
        handleSessionExpired();
        return;
      }
      if (outcome.kind === 'error') {
        const id = showFailure(outcome);
        if (outcome.retry) setRedoMap((m) => ({ ...m, [id]: 'history' }));
        return;
      }
      answered();
      const rows = outcome.messages as HistoryRow[];
      setTurns((prev) => {
        const seen = new Set(prev.map((t) => t.id));
        const fresh: ChatTurn[] = rows
          .filter((row) => !seen.has(row.id))
          .map((row) =>
            row.role === 'user'
              ? {
                  role: 'user',
                  id: row.id,
                  text: row.text,
                  createdAt: row.createdAt,
                  ...(row.media ? { media: row.media } : {}),
                }
              : {
                  role: 'assistant',
                  id: row.id,
                  text: row.text,
                  components: row.components ?? null,
                  event: null,
                  createdAt: row.createdAt,
                  ...(row.media ? { media: row.media } : {}),
                },
          );
        return [...prev, ...fresh].sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      });
      setHistoryLoaded(true);
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, historyAttempt]);

  // Part A — GET /messages/stream is the live view of this user's
  // unified conversation. Every message — inbound Telegram/WhatsApp turns
  // the user sent from their phone, agent replies, reach_out triggers,
  // scheduled report drops — is written through one dispatchMessage door
  // and published to the per-user SSE registry this route consumes. The
  // SPA renders them ALL into the one thread.
  // Live messages append (sorted by createdAt); `replay` events backfill
  // anything missed while disconnected (the route reads ?since=<id> as the
  // tombstone). Heartbeats are silent — we trust the connection while they
  // arrive.
  useEffect(() => {
    const SINCE_KEY = `dsh_stream_since_${userId}`;
    const since = localStorage.getItem(SINCE_KEY);
    const url = since
      ? `/messages/stream?since=${encodeURIComponent(since)}`
      : '/messages/stream';
    const es = new EventSource(url, { withCredentials: true });

    const handleIncoming = (raw: MessageEvent<string>, kind: 'live' | 'replay') => {
      let parsed: {
        id: string;
        // Part A: the registry now carries every message, so the
        // envelope is widened with role. An inbound turn (a Telegram message
        // the user sent) is role 'user'; an agent reply is 'assistant'.
        role: 'user' | 'assistant' | 'system';
        source: string;
        content: string | null;
        threadId: string | null;
        createdAt: string;
        // N1: pushes carry their rendered composition (chart/stat/
        // list). Heartbeat/ready frames and text-only turns omit it → null.
        components?: Component[] | null;
        media?: ChatMedia | null;
      };
      try {
        parsed = JSON.parse(raw.data);
      } catch {
        return;
      }
      // No threadId filter (Part A): the registry is per-user, so
      // every message on this stream belongs to this user — across
      // Telegram / WhatsApp / web. The prior "thread per user per channel"
      // scoping is gone; this is one conversation.

      // Build the matching bubble side. A role 'user' message (an inbound
      // Telegram/WhatsApp turn) renders on the user side; 'assistant' (and
      // the rare 'system') on the agent side. An image the message carries
      // (an originated one, e.g. the pairing picture) arrives as a signed
      // URL in `media`; /chat/history re-signs media URLs after a reload.
      const turn: ChatTurn = turnFromStream(parsed);

      // `upsertById` resolves the dots placeholder in place (or
      // appends a cross-channel / replayed turn). One ordered channel — no race.
      setTurns((prev) => upsertById(prev, turn));

      // The awaited reply (or its Hebrew-error fallback) has landed —
      // end the in-flight wait so the composer re-enables. A reply for any
      // other turn (cross-channel, a sibling tab's send) doesn't touch it.
      if (turn.role === 'assistant' && awaitingReplyId.current === turn.id) {
        awaitingReplyId.current = null;
        clearReplyWatchdog();
        setPending(false);
      }

      // Persist the tombstone — only for `live` events. Replays restate the
      // tombstone we already have, so writing it again is a no-op.
      if (kind === 'live') {
        localStorage.setItem(SINCE_KEY, parsed.id);
      }
    };

    es.addEventListener('message', (e) => handleIncoming(e as MessageEvent<string>, 'live'));
    es.addEventListener('replay', (e) => handleIncoming(e as MessageEvent<string>, 'replay'));
    // `ready` and `heartbeat` are silent — they keep the connection alive
    // and confirm liveness. EventSource auto-reconnects on transient errors.

    // 4.1 — the stream errored. EventSource auto-reconnects on a
    // transient blip, but a server-closed stream from an expired session would
    // loop forever. Debounce a single /me probe to tell the two apart: a 401
    // means the session died → surface re-auth; anything else is transient and
    // EventSource handles its own reconnect. The guard ensures one probe per
    // error burst (EventSource fires `error` repeatedly while reconnecting).
    let probeTimer: ReturnType<typeof setTimeout> | null = null;
    es.addEventListener('error', () => {
      if (probeTimer) return;
      probeTimer = setTimeout(() => {
        probeTimer = null;
        fetch('/me', { credentials: 'include' })
          .then((res) => {
            if (res.status === 401) setSessionExpired(true);
          })
          .catch(() => {
            /* offline — the online/offline listener owns the banner */
          });
      }, 3000);
    });

    return () => {
      if (probeTimer) clearTimeout(probeTimer);
      es.close();
    };
  }, [userId]);

  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({
      top: scroller.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [turns.length, pending]);

  // Persist draft so an interrupted send (or session expiry) doesn't lose user
  // input — the composer rehydrates from this identity's own draft key on
  // mount, including after a re-login (4.1). Keyed by identity so the same
  // persistence cannot surface it to the next person on a shared browser.
  useEffect(() => {
    const key = draftKeyFor(userId);
    if (draft) localStorage.setItem(key, draft);
    else localStorage.removeItem(key);
  }, [draft, userId]);

  // 4.1 — reflect real connectivity in the NetworkBanner. Send
  // failures already flip `connected` (send()/sendAction()); these listeners
  // catch a drop that happens between sends (e.g. wifi loss while idle) and
  // clear the banner when connectivity returns.
  useEffect(() => {
    const onOnline = () => setConnected(true);
    const onOffline = () => setConnected(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // 4.1 — clear the reply watchdog on unmount.
  useEffect(() => () => clearReplyWatchdog(), []);

  // `?q=<text>` deep-link drain. The module-level IIFE stashed it before
  // React mounted; once we're authenticated and Chat renders, drain it once.
  //
  // (REM-3 / M6): the deep-link no longer AUTO-SENDS. A crafted
  // `?q=<attacker text>` link would otherwise dispatch attacker-controlled text
  // as the victim's first post-auth message — their agent would then act on it
  // (a phishing / prompt-injection primitive). The operator-decided fix: PREFILL
  // the drained text into the composer draft and require the user to explicitly
  // tap send. The deep-link convenience stays; only the auto-dispatch is removed.
  //
  // Still GATED on `hydrationDone` (the prefill waits for history to land
  // so the composer is interactive). The useRef single-fire guard prevents a
  // re-prefill across re-renders; the 10k truncation happened at capture (IIFE).
  const pendingQFired = useRef(false);
  useEffect(() => {
    if (!hydrationDone || pendingQFired.current) return;
    const q = drainPendingQ(userId);
    if (q) {
      pendingQFired.current = true;
      setDraft(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationDone]);

  // `/r/<cid>` deep-link drain. Mirrors the `?q=` pattern —
  // gated on historyLoaded (a failed history keeps the stash), single-fire ref. Finds the turn whose canonical
  // id (meta.cid ?? id) matches the stashed cid; if found,
  // scrolls into view and pulses a ring highlight on the bubble for 1.5s.
  // If not found in the initial /chat/history page, surfaces a brief
  // Hebrew notice; auto-paginate-back is deferred to a polish iteration.
  const [highlightedCid, setHighlightedCid] = useState<string | null>(null);
  const [pendingRNotFound, setPendingRNotFound] = useState(false);
  const bubbleRefs = useRef<Map<string, HTMLLIElement>>(new Map());
  const pendingRFired = useRef(false);
  useEffect(() => {
    if (!historyLoaded || pendingRFired.current) return;
    const cid = localStorage.getItem(pendingRKeyFor(userId));
    if (!cid || !cid.trim()) return;
    pendingRFired.current = true;
    localStorage.removeItem(pendingRKeyFor(userId));
    // Match canonical-id contract: turn ids equal `meta.cid ?? row.id`
    // so a direct `t.id === cid` lookup hits both shapes (cid-bearing rows
    // expose the cid as the SPA-facing id; legacy rows fall back to row.id).
    const target = turns.find((t) => t.id === cid);
    if (target) {
      setHighlightedCid(cid);
      // Defer scroll one frame so the ring class lands before the smooth
      // scroll starts — visually the pulse animates concurrently with the
      // scroll-into-view.
      requestAnimationFrame(() => {
        const node = bubbleRefs.current.get(cid);
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      // Clear highlight after the pulse animation completes (1.5s feels
      // right — long enough to be noticed without lingering).
      const t = setTimeout(() => setHighlightedCid(null), 1500);
      return () => clearTimeout(t);
    }
    // Bubble not in current turns — surface a brief notice. Auto-pagination
    // (load more pages until found) is a polish iteration; v1 just tells
    // the user to scroll up to load more history.
    setPendingRNotFound(true);
    const t = setTimeout(() => setPendingRNotFound(false), 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyLoaded]);

  // 4.1 — the in-flight-reply watchdog. Cleared when the reply lands
  // (the stream consumer), on a fresh send, or on unmount.
  const clearReplyWatchdog = (): void => {
    if (replyWatchdog.current) {
      clearTimeout(replyWatchdog.current);
      replyWatchdog.current = null;
    }
  };
  const startReplyWatchdog = (
    replyCid: string,
    retry?: SendRecord,
  ): void => {
    clearReplyWatchdog();
    replyWatchdog.current = setTimeout(() => {
      replyWatchdog.current = null;
      // Only fire if THIS reply is still the one we're awaiting (not resolved).
      if (awaitingReplyId.current !== replyCid) return;
      awaitingReplyId.current = null;
      // Replace the dots placeholder (same id) with a Hebrew timeout message;
      // attach a retry payload so "נסו שוב" re-sends the original turn.
      setTurns((t) =>
        t.map((turn) =>
          turn.id === replyCid &&
          turn.role === 'assistant' &&
          'pending' in turn &&
          turn.pending === true
            ? {
                role: 'assistant',
                id: replyCid,
                text: REPLY_TIMEOUT_TEXT,
                components: null,
                event: null,
                createdAt: new Date().toISOString(),
              }
            : turn,
        ),
      );
      if (retry) setRetryMap((m) => ({ ...m, [replyCid]: retry }));
      setPending(false);
    }, REPLY_TIMEOUT_MS);
  };

  // 4.1 — a 401 from any authenticated request (or the stream) means
  // the session expired mid-conversation. Preserve the unsent text as the draft
  // (it persists to localStorage and is restored after re-login), drop the
  // in-flight lock + watchdog, and surface the re-auth overlay.
  const handleSessionExpired = (unsentText?: string): void => {
    clearReplyWatchdog();
    awaitingReplyId.current = null;
    if (unsentText && unsentText.trim()) setDraft(unsentText);
    setPending(false);
    setSessionExpired(true);
  };

  // A retry passes the original record: same cid, same text, same media —
  // never the composer's current draft or attachment.
  const send = async (text: string, resend?: SendRecord): Promise<void> => {
    const trimmed = resend ? resend.text : text.trim();
    const stagedAttachment: ChatMedia | null = resend
      ? (resend.media ?? null)
      : attachment;
    // image-only send is allowed; require either text OR an attachment.
    if ((!trimmed && !stagedAttachment) || pending) return;
    setPending(true);
    if (!resend) {
      setDraft('');
      setAttachment(null);
      setAttachmentError(null);
    }

    // canonical-id contract.
    //
    // Generate one UUID used as BOTH the optimistic turn `id` AND the POST
    // body's `client_msg_id`. Backend stores it in `messages.meta.cid` and
    // `/chat/history` returns it as the row's `id`, so optimistic↔hydrated
    // dedup is exact (no heuristic, no time window).
    //
    // Persist BEFORE the network call so a crash mid-send is recoverable
    // (per industry best practice — Stripe/Slack/Convex idempotency-key
    // pattern). Cleared on success in either branch below.
    //
    // When called from `retry()`, the caller passes the original
    // attempt's `cid` so the backend recognises this as the SAME logical
    // user action and short-circuits via the `dedup_hit` SSE event. Fresh
    // user sends (no override) generate a new cid as before.
    const cid = resend?.cid ?? crypto.randomUUID();
    const inflightKey = `${INFLIGHT_KEY_PREFIX}${threadId}`;
    const record = makeRecord(trimmed, cid, stagedAttachment);
    try {
      localStorage.setItem(inflightKey, inflightJson(record));
    } catch {
      /* full storage / disabled — proceed without recovery scaffold */
    }
    // On a retry, the optimistic user bubble for this cid is
    // already in `turns` (the original attempt rendered it). Skip the
    // duplicate insertion — the bubble stays visible across the retry
    // round-trip; the assistant response will append below.
    setTurns((t) =>
      t.some((turn) => turn.id === cid)
        ? t
        : [...t, userTurnFrom(cid, trimmed, stagedAttachment ?? undefined)],
    );

    try {
      const answer = await readAnswer(
        await fetch('/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(chatBody(record, threadId)),
        credentials: 'include',
      }).catch((e: unknown) => e),
      );
      const outcome = chatOutcome(answer);
      // 4.1 — session expired mid-conversation. Preserve the draft +
      // re-auth; drop the optimistic user bubble (a full reload follows on
      // re-login, after which the restored draft re-sends).
      if (outcome.kind === 'unauthorized') {
        setTurns((t) => t.filter((turn) => turn.id !== cid));
        try {
          localStorage.removeItem(inflightKey);
        } catch {
          /* ignore */
        }
        handleSessionExpired(trimmed);
        return;
      }
      if (outcome.kind === 'error') {
        // The server answered (or the request never left): show the message
        // for this answer; only a request that never reached it is offline.
        const id = showFailure(outcome);
        if (outcome.retry) setRetryMap((m) => ({ ...m, [id]: record }));
        try {
          localStorage.removeItem(inflightKey);
        } catch {
          /* ignore */
        }
        setPending(false);
        return;
      }

      // POST /chat answers 202 { cid } (accepted; the reply arrives on
      // /messages/stream keyed by this cid; keep `pending` true) or
      // 200 { deduped: true } (a same-cid duplicate; no placeholder).
      answered();
      try {
        localStorage.removeItem(inflightKey);
      } catch {
        /* ignore */
      }

      if (outcome.kind === 'deduped') {
        // Nothing to place; release the single-flight guard. Any reply
        // arrives on /messages/stream.
        setPending(false);
        return;
      }

      // 202 — reserve the slot and await the reply on /messages/stream. The
      // dots placeholder is keyed by the reply cid; the stream consumer
      // replaces it in place and clears `pending` when it arrives.
      const replyCid = outcome.cid;
      awaitingReplyId.current = replyCid;
      // 4.1 — arm the timeout watchdog; "נסו שוב" re-sends this turn.
      startReplyWatchdog(replyCid, record);
      setTurns((t) =>
        t.some((turn) => turn.id === replyCid)
          ? t
          : [...t, pendingAssistantTurn(replyCid)],
      );
      // `pending` intentionally stays true here.
    } catch {
      // An unexpected local failure (answers are classified above). The reply
      // isn't coming, so show a retryable error bubble and release the guard.
      // The optimistic user bubble stays; "נסו שוב" re-sends with the same cid.
      const id = showFailure(LOCAL_FAILURE);
      setRetryMap((m) => ({ ...m, [id]: record }));
      try {
        localStorage.removeItem(inflightKey);
      } catch {
        /* ignore */
      }
      setPending(false);
    }
  };

  // confirm-component / list-row action_id round-trip.
  //
  // Fired when the user taps an interactive surface inside an assistant
  // bubble that carries an action_id (currently: ConfirmRender's אישור
  // button + list rows with `row.action_id`). Posts /chat with
  // {action_id, message:'', thread_id} — NO local user bubble (the
  // confirm card's "אישור · 14:32" state + the list row's tap-then-disabled
  // affordance are the user-side signal that the action fired). The agent's
  // result component lands as a new assistant bubble below.
  //
  // Pending guard: same as send() — drops dup taps before the round-trip
  // returns. ConfirmRender + ListRender also render disabled while pending
  // (UX backstop).
  const sendAction = async (action_id: string): Promise<void> => {
    if (pending) return;
    setPending(true);
    try {
      const outcome = chatOutcome(
        await readAnswer(
          await fetch('/chat', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              action_id,
              thread_id: threadId,
            }),
            credentials: 'include',
          }).catch((e: unknown) => e),
        ),
      );
      // 4.1 — session expired during a confirm/action round-trip.
      if (outcome.kind === 'unauthorized') {
        handleSessionExpired();
        return;
      }
      if (outcome.kind === 'error') {
        showFailure(outcome);
        setPending(false);
        return;
      }

      // Same 202 {cid} contract as send(). No user bubble — the
      // confirm card's tapped state is the user-side signal. On 202 insert ONE
      // dots placeholder keyed by the reply cid; the stream consumer resolves
      // it in place and clears `pending`.
      answered();
      if (outcome.kind === 'deduped') {
        setPending(false);
        return;
      }
      const replyCid = outcome.cid;
      awaitingReplyId.current = replyCid;
      // 4.1 — arm the timeout watchdog. No retry payload: an action
      // round-trip has no re-sendable user text (the source confirm/list card
      // carries its own re-tap affordance).
      startReplyWatchdog(replyCid);
      setTurns((t) =>
        t.some((turn) => turn.id === replyCid)
          ? t
          : [...t, pendingAssistantTurn(replyCid)],
      );
      // `pending` stays true until the reply lands on /messages/stream.
    } catch {
      showFailure(LOCAL_FAILURE);
      setPending(false);
    }
  };

  const retry = (errorId: string): void => {
    const redo = redoMap[errorId];
    if (redo) {
      setTurns((t) => t.filter((turn) => turn.id !== errorId));
      setRedoMap((m) => {
        const { [errorId]: _, ...rest } = m;
        return rest;
      });
      if (redo === 'logout') void logout();
      else setHistoryAttempt((n) => n + 1);
      return;
    }
    const entry = retryMap[errorId];
    if (!entry) return;
    setTurns((t) => t.filter((turn) => turn.id !== errorId));
    setRetryMap((m) => {
      const { [errorId]: _, ...rest } = m;
      return rest;
    });
    // Reuse the original `cid` so the backend's
    // `idx_messages_org_channel_thread_external` dedups this as the same logical user
    // action. The first attempt's `respond()` may still be in flight; its
    // result arrives via `/messages/stream` regardless of which POST
    // "wins" the dedup race.
    void send(entry.text, entry);
  };

  // Sign-out is complete only when the server confirms it (logoutOutcome).
  // Until then the member stays signed in with the draft kept, and the
  // error bubble's "נסו שוב" re-posts /auth/logout.
  const logout = async (): Promise<void> => {
    const outcome = logoutOutcome(
      await readAnswer(
        await fetch('/auth/logout', {
          method: 'POST',
          credentials: 'include',
        }).catch((e: unknown) => e),
      ),
    );
    if (outcome.kind === 'error') {
      const id = showFailure(outcome);
      if (outcome.retry) setRedoMap((m) => ({ ...m, [id]: 'logout' }));
      return;
    }
    // The draft and both deep-link stashes go, along with the marker naming
    // this browser's last identity, so the next person starts clean.
    releaseIdentity(userId);
    try {
      localStorage.removeItem(`dsh_thread_${userId}`);
    } catch {
      /* storage disabled — ignore */
    }
    // Full reload routes through AuthGate; cookie is cleared by the server,
    // /me returns 401, AuthScreen renders.
    window.location.assign('/');
  };

  // Upload the picked file to R2 immediately (so the
  // thumbnail above the composer reflects a real server URL); stage it as
  // `attachment` until the user hits send.
  const uploadFile = async (file: File): Promise<void> => {
    setAttachmentError(null);
    setUploadingAttachment(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/upload', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      // 4.1 — session expired while picking a file.
      if (res.status === 401) {
        handleSessionExpired();
        return;
      }
      if (!res.ok) {
        const body = (await res
          .json()
          .catch(() => ({}))) as { error?: string; message?: string };
        const reason =
          body.message ??
          (res.status === 415
            ? 'סוג קובץ לא נתמך'
            : res.status === 413
            ? 'הקובץ גדול מדי'
            : res.status === 503
            ? 'אחסון לא זמין כרגע'
            : 'העלאה נכשלה');
        setAttachmentError(reason);
        return;
      }
      const data = (await res.json()) as { key: string; mime: string; url: string };
      setAttachment({ key: data.key, url: data.url, mime: data.mime });
    } catch {
      setAttachmentError('שגיאת רשת — נסו שוב.');
    } finally {
      setUploadingAttachment(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <NetworkBanner
        connected={connected}
        onRetry={() => lastErrorId && retry(lastErrorId)}
        retryEnabled={!pending}
      />
      {sessionExpired ? (
        <SessionExpiredOverlay onReauth={() => window.location.assign('/')} />
      ) : null}
      {impersonating ? <ImpersonationBanner impersonating={impersonating} /> : null}
      <Header
        onLogout={() => void logout()}
        canImpersonate={canImpersonate}
      />

      <main
        ref={scroller}
        className="flex-1 overflow-y-auto px-4 py-3"
        aria-live="polite"
      >
        <div className="mx-auto max-w-[720px]">
          {turns.length === 0 ? (
            <EmptyState
              suggestions={suggestions}
              onSuggestionTap={(s) => void send(s)}
              canViewGroup={canViewGroup}
            />
          ) : (
            <MessageList
              turns={turns}
              pending={pending}
              retryMap={retryMap}
              redoMap={redoMap}
              onRetry={retry}
              onAction={(id) => void sendAction(id)}
              highlightedCid={highlightedCid}
              bubbleRefs={bubbleRefs}
            />
          )}
          {pendingRNotFound ? (
            <div
              className="mx-auto mt-3 max-w-[85%] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-center text-sm text-[var(--color-muted)] shadow-[var(--shadow-bubble)]"
              role="status"
            >
              לא נמצא דוח קודם — גוללו למעלה כדי לטעון היסטוריה
            </div>
          ) : null}
        </div>
      </main>

      <Composer
        draft={draft}
        setDraft={setDraft}
        onSubmit={() => void send(draft)}
        pending={pending}
        attachment={attachment}
        attachmentError={attachmentError}
        uploadingAttachment={uploadingAttachment}
        onPickFile={(file) => void uploadFile(file)}
        onClearAttachment={() => {
          setAttachment(null);
          setAttachmentError(null);
        }}
        onSlashSend={(text) => void send(text)}
      />
    </div>
  );
};

// ---- Header ---------------------------------------------------------------

const Header = ({
  onLogout,
  canImpersonate,
}: {
  onLogout: () => void;
  // (live-test pass 2): gates the "החלפת זהות" menu item. /me
  // reports false under impersonation, so the picker is never reachable
  // from inside an impersonation session.
  canImpersonate: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click outside / Escape → dismiss.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // White-label wordmark: the signed-in org's brand name, resolved from the
  // org-config fetched at boot — the same source AuthScreen + the browser tab
  // use. Org-less / platform-admin contexts fall back to the platform brand.
  const cfg = getOrgConfig();
  const orgName = cfg.brand?.name ?? cfg.name ?? 'דרישלום';

  return (
    <header className="flex h-14 shrink-0 items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-card)] px-4">
      {/* RIGHT in RTL = DOM-first. Wordmark sits on the visual right. */}
      <h1 className="text-base font-bold text-[var(--color-primary)]">{orgName}</h1>
      {/* LEFT in RTL = DOM-last. Status pill on the visual left. */}
      <div ref={wrapperRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="פתח תפריט חשבון"
          title="חשבון"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-muted)] hover:bg-[var(--color-primary-light)] hover:text-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
        >
          {/* heroicons outline — user (same mark as the user-side chat avatar) */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a7.5 7.5 0 1115 0" />
          </svg>
        </button>
        {open ? (
          <div
            role="menu"
            className="absolute end-0 top-full z-50 mt-2 min-w-[180px] rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] py-1 shadow-[var(--shadow-frame)]"
            style={{ animation: 'dsh-pop-in 150ms ease-out' }}
          >
            {/*: gated on can_impersonate — opens the picker. */}
            {canImpersonate ? (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  setPickerOpen(true);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-surface)]"
              >
                {/* heroicons outline — user-circle */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>החלפת זהות</span>
              </button>
            ) : null}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onLogout();
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--color-bad)] hover:bg-[var(--color-surface)]"
            >
              {/* heroicons outline — arrow-right-on-rectangle (logout) */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              <span>התנתקות</span>
            </button>
          </div>
        ) : null}
      </div>
      {pickerOpen ? (
        <ImpersonationPicker onClose={() => setPickerOpen(false)} />
      ) : null}
    </header>
  );
};

// ---- Empty state ----------------------------------------------------------

const EmptyState = ({
  suggestions,
  onSuggestionTap,
  canViewGroup,
}: {
  suggestions: string[];
  onSuggestionTap: (s: string) => void;
  // Gates the group-flavoured greeting.
  canViewGroup: boolean;
}) => {
  // Same white-label source as the header wordmark — the org's brand name,
  // falling back to the platform brand for org-less contexts.
  const cfg = getOrgConfig();
  const orgName = cfg.brand?.name ?? cfg.name ?? 'דרישלום';
  return (
    <div className="flex flex-col items-center gap-3 pt-12 text-center">
      <div className="text-3xl font-bold text-[var(--color-primary)]">{orgName}</div>
      <div
        className="mt-2 h-12 w-12 rounded-full border-2 border-dashed border-[var(--color-muted)]"
        aria-hidden="true"
      />
      {/* S6 — the group greeting only for a user who can actually see
          a group. Everyone else gets the neutral opener; asking someone about
          "the group" when their visibility is their own data alone reads as
          the product not knowing who it's talking to. */}
      <div className="mt-2 text-base text-[var(--color-muted)]">
        {canViewGroup ? 'מה קורה עם הקבוצה? 💫' : 'מה שלומכם היום? 💫'}
      </div>
      <div className="text-sm text-[var(--color-muted)]">
        בחרו נושא או פשוט שאלו
      </div>
      <div className="mt-4 flex w-full max-w-sm flex-col gap-2">
        {acceptSuggestions(suggestions).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onSuggestionTap(s)}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-sm hover:bg-[var(--color-primary-light)]"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

// ---- Message list ---------------------------------------------------------

const MessageList = ({
  turns,
  pending,
  retryMap,
  redoMap,
  onRetry,
  onAction,
  highlightedCid,
  bubbleRefs,
}: {
  turns: ChatTurn[];
  pending: boolean;
  retryMap: Record<string, { text: string; cid: string }>;
  redoMap: Record<string, 'history' | 'logout'>;
  onRetry: (errorId: string) => void;
  // Fires when a user taps a row in a `list` component
  // or the אישור button in a `confirm` component (any surface carrying
  // an action_id). The handler routes to App.tsx's sendAction().
  onAction?: (action_id: string) => void;
  // When a /r/<cid> deep-link drains and the turn is found,
  // the matching bubble pulses a primary-color ring for 1.5s. `bubbleRefs`
  // is the parent's per-cid `<li>` ref map used for scrollIntoView.
  highlightedCid?: string | null;
  bubbleRefs?: React.MutableRefObject<Map<string, HTMLLIElement>>;
}) => (
  <ul className="space-y-3">
    {turns.map((t) => (
      <Bubble
        key={t.id}
        turn={t}
        retryFor={
          t.role === 'assistant' && (retryMap[t.id] || redoMap[t.id])
            ? () => onRetry(t.id)
            : null
        }
        onAction={onAction}
        pending={pending}
        highlighted={highlightedCid === t.id}
        liRef={(node) => {
          if (!bubbleRefs) return;
          if (node) bubbleRefs.current.set(t.id, node);
          else bubbleRefs.current.delete(t.id);
        }}
      />
    ))}
  </ul>
);

const Bubble = ({
  turn,
  retryFor,
  onAction,
  pending,
  highlighted,
  liRef,
}: {
  turn: ChatTurn;
  retryFor: (() => void) | null;
  // Propagated to ComponentSwitch which routes to the
  // confirm/list interactive surfaces.
  onAction?: (action_id: string) => void;
  pending?: boolean;
  // When true, the bubble renders with a primary-color ring
  // for the highlight pulse triggered by the /r/<cid> deep-link drain.
  highlighted?: boolean;
  // parent-supplied ref so the deep-link drain can scrollIntoView
  // the matching bubble after hydration.
  liRef?: (node: HTMLLIElement | null) => void;
}) => {
  const isUser = turn.role === 'user';
  // Protocol switched from singular `component?` to plural `components: Component[] | null`.
  // Render every component in the array as a sequence inside one bubble (e.g. show_report's
  // hero stat → mini-strip → chart → narrative composition).
  const components = !isUser && 'components' in turn ? turn.components ?? [] : [];
  const hasComponents = components.length > 0;
  // The dots placeholder. `send()`/`sendAction()` insert a `pending`
  // assistant turn on POST /chat's 202; render it as the dots-only typing
  // indicator (assistant side). `upsertById` replaces it in place when the
  // real reply (same cid) lands on /messages/stream — the loader IS the slot.
  const isPending = !isUser && 'pending' in turn && turn.pending === true;
  // Bubble alignment in RTL (— standard chat-app convention):
  //   user bubble → visually RIGHT (DOM justify-start, the inline-start in RTL)
  //   AI bubble   → visually LEFT  (DOM justify-end, the inline-end in RTL)
  // Matches WhatsApp / iMessage / Telegram Hebrew conventions — "me on the
  // right, them on the left" is universal across chat apps.
  const align = isUser ? 'flex justify-start' : 'flex justify-end';

  // The width cap belongs to the flex-col wrapper below (the one
  // introduced to stack the bubble over its timestamp) and is applied
  // there ONCE. Repeating it here nested it: 85% of the wrapper, which is
  // itself 85% of the row, is an effective 72.25%. Measured at a 390px
  // viewport that left 227px of text — narrow enough to wrap a short Hebrew
  // greeting mid-phrase. The bubble now stretches to the wrapper, and the
  // wrapper still shrink-to-fits short messages, so short bubbles still hug.
  const userCls =
    'w-fit rounded-2xl bg-[var(--color-user-bubble)] px-4 py-2 text-sm text-white shadow-[var(--shadow-bubble)]';
  const aiTextCls =
    'w-fit rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]';
  // The card bubble deliberately keeps NO width class: it must stretch to the
  // wrapper so a `w-full` chart inside it resolves against a real width rather
  // than collapsing under fit-content sizing.
  const aiCardCls =
    'rounded-2xl bg-[var(--color-primary-light)] px-4 py-2 text-sm text-[var(--color-text)] shadow-[var(--shadow-bubble)]';

  // Long assistant narratives collapse to הצג עוד per design-system §4.
  // User bubbles never collapse — they're always the user's own text and
  // hiding it would feel like the system suppressed their message.
  // Assistant text is markdown — render via react-markdown with RTL-aware
  // padding (ps-5 = padding-inline-start, sits on the right in RTL where
  // bullets render). User text stays plain to avoid surprises.
  const userMedia = isUser ? mediaOf(turn) : undefined;
  const assistantMedia = isUser ? undefined : mediaOf(turn);
  const TextNode = isUser ? (
    <div>
      {userMedia ? (
        userMedia.mime.startsWith('audio/') ? (
          // A sent voice note — playable, no thumbnail.
          <audio
            controls
            src={userMedia.url}
            className="mb-2 w-full max-w-[260px]"
          />
        ) : (
          <img
            src={userMedia.url}
            alt=""
            className="mb-2 max-h-72 w-auto rounded-lg object-cover"
          />
        )
      ) : null}
      {turn.text ? <div>{turn.text}</div> : null}
    </div>
  ) : (
    <ShowMore showCollapse>
      {assistantMedia ? (
        <img
          src={assistantMedia.url}
          alt=""
          className="mb-2 max-h-72 w-auto rounded-lg object-cover"
        />
      ) : null}
      <div className="[&_p]:my-1 [&_strong]:font-bold [&_em]:italic [&_ul]:my-1 [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:my-0.5 [&_a]:text-[var(--color-primary)] [&_a]:underline [&_code]:rounded [&_code]:bg-[var(--color-card)] [&_code]:px-1 [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-[var(--color-border)] [&_th]:bg-[var(--color-card)] [&_th]:px-2 [&_th]:py-1 [&_th]:text-start [&_th]:font-bold [&_td]:border [&_td]:border-[var(--color-border)] [&_td]:px-2 [&_td]:py-1 [&_td]:text-start">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{turn.text}</ReactMarkdown>
      </div>
    </ShowMore>
  );

  const bubble = isPending ? (
    // dots-only typing indicator in the standard assistant bubble
    // shell. No text, no components, no retry affordance. Replaced in place by
    // the real reply when it arrives via the /messages/stream consumer.
    <div
      className={aiTextCls}
      role="status"
      aria-live="polite"
      aria-label="חושבת..."
    >
      <span className="inline-flex gap-1" aria-hidden="true">
        <span className="thinking-dot [animation-delay:0ms]" />
        <span className="thinking-dot [animation-delay:150ms]" />
        <span className="thinking-dot [animation-delay:300ms]" />
      </span>
    </div>
  ) : (
    <div className={isUser ? userCls : hasComponents ? aiCardCls : aiTextCls}>
      {TextNode}
      {components.map((c, i) => (
        <ComponentSwitch
          key={i}
          component={c}
          onAction={onAction}
          pending={pending}
        />
      ))}
      {retryFor ? (
        <button
          type="button"
          onClick={retryFor}
          className="mt-2 rounded-md bg-[var(--color-card)] px-3 py-1 text-xs font-bold text-[var(--color-primary)]"
        >
          נסו שוב
        </button>
      ) : null}
    </div>
  );

  // DOM order positions the avatar on the outer edge of the bubble:
  //   user (justify-start → visual RIGHT in RTL): [avatar, bubble] → avatar
  //     ends up at the far-right, bubble to its left.
  //   agent (justify-end → visual LEFT in RTL): [bubble, avatar] → avatar
  //     at the far-left, bubble to its right.
  // When `highlighted` is true, wrap the bubble row in a primary
  // ring for the magic-link landing pulse. `transition-shadow` lets the ring
  // fade in/out gracefully as `highlighted` toggles back to false 1.5s after
  // mount (parent clears the state).
  const highlightCls = highlighted
    ? 'rounded-2xl ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-surface)] transition-shadow duration-300'
    : 'transition-shadow duration-300';
  // date+time under every bubble (issue acceptance: "date+time,
  // he-IL locale, 24h format"), no day-grouping. Reuses ComponentSwitch's
  // formatTimestamp (`HH:MM DD/MM`) as-is rather than adding a second
  // formatter. Hidden on the pending dots-only placeholder — it has no
  // real send time yet.
  const timeNode =
    !isPending && 'createdAt' in turn ? (
      <div
        className={`px-1 text-xs tabular-nums text-[var(--color-muted)] ${isUser ? 'text-end' : 'text-start'}`}
      >
        {formatTimestamp(turn.createdAt)}
      </div>
    ) : null;
  return (
    <li
      ref={liRef}
      className={`${align} items-end gap-2 ${highlightCls}`}
    >
      {isUser ? (
        <>
          <Avatar kind="user" />
          <div className="flex max-w-[85%] flex-col">
            {bubble}
            {timeNode}
          </div>
        </>
      ) : (
        <>
          <div className={hasComponents ? 'flex max-w-[92%] flex-col' : 'flex max-w-[85%] flex-col'}>
            {bubble}
            {timeNode}
          </div>
          <Avatar kind="agent" />
        </>
      )}
    </li>
  );
};

// ---- Avatars --------------------------------------------------------------

const Avatar = ({ kind }: { kind: 'user' | 'agent' }) => {
  const base =
    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full';
  if (kind === 'user') {
    return (
      <div
        className={`${base} bg-[var(--color-surface)] text-[var(--color-muted)] border border-[var(--color-border)]`}
        aria-hidden="true"
      >
        {/* Generic person icon — heroicons outline */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
        >
          <path d="M15.75 7.5a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.25a7.5 7.5 0 1115 0" />
        </svg>
      </div>
    );
  }
  // Agent — placeholder logo mark (swappable later).
  return (
    <div
      className={`${base} bg-[var(--color-primary)] text-white`}
      aria-hidden="true"
    >
      <span className="text-xs font-bold">ד</span>
    </div>
  );
};

// ---- Composer -------------------------------------------------------------

const Composer = ({
  draft,
  setDraft,
  onSubmit,
  pending,
  attachment,
  attachmentError,
  uploadingAttachment,
  onPickFile,
  onClearAttachment,
  onSlashSend,
}: {
  draft: string;
  setDraft: (s: string) => void;
  onSubmit: () => void;
  pending: boolean;
  attachment: ChatMedia | null;
  attachmentError: string | null;
  uploadingAttachment: boolean;
  onPickFile: (file: File) => void;
  onClearAttachment: () => void;
  onSlashSend: (text: string) => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!overflowWrapperRef.current?.contains(e.target as Node)) {
        setOverflowOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverflowOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [overflowOpen]);

  useEffect(() => {
    if (draft.startsWith('/')) setOverflowOpen(true);
  }, [draft]);
  const len = draft.length;
  const showCounter = len >= COUNTER_THRESHOLD;
  const counterColor =
    len >= COUNTER_WARN_THRESHOLD
      ? 'text-[var(--color-warn)]'
      : 'text-[var(--color-muted)]';
  // Send disabled until we have a non-empty draft OR a staged attachment.
  // Image-only sends are allowed.
  const canSend = (!!draft.trim() || !!attachment) && !pending && !uploadingAttachment;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="border-t border-[var(--color-border)] bg-[var(--color-card)] px-3 py-2"
    >
      {/* Attachment-staging strip — visible above the input row when a file
          is uploading or already uploaded. Replaces nothing in the input
          row itself (keeps the composer layout stable). */}
      {(attachment || uploadingAttachment || attachmentError) && (
        <div className="mx-auto mb-2 max-w-[720px]">
          {uploadingAttachment ? (
            <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs text-[var(--color-muted)]">
              <span className="inline-flex gap-1" aria-hidden="true">
                <span className="thinking-dot [animation-delay:0ms]" />
                <span className="thinking-dot [animation-delay:150ms]" />
                <span className="thinking-dot [animation-delay:300ms]" />
              </span>
              <span>מעלה קובץ...</span>
            </div>
          ) : attachment ? (
            <div className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-1 pr-2">
              {attachment.mime.startsWith('audio/') ? (
                // Audio has no thumbnail — a mic chip stands in.
                <span className="inline-flex h-12 items-center gap-2 px-2 text-sm text-[var(--color-muted)]">
                  🎤 הודעה קולית
                </span>
              ) : (
                <img
                  src={attachment.url}
                  alt=""
                  className="h-12 w-12 rounded object-cover"
                />
              )}
              <button
                type="button"
                onClick={onClearAttachment}
                aria-label="הסר קובץ"
                title="הסר קובץ"
                className="rounded-full bg-[var(--color-card)] px-2 text-sm text-[var(--color-muted)] hover:text-[var(--color-bad)]"
              >
                ×
              </button>
            </div>
          ) : null}
          {attachmentError ? (
            <div
              role="alert"
              className="mt-1 text-xs text-[var(--color-bad)]"
            >
              {attachmentError}
            </div>
          ) : null}
        </div>
      )}
      <div className="mx-auto flex max-w-[720px] items-stretch gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,audio/ogg,audio/mpeg,audio/mp4,audio/x-m4a,audio/aac,audio/wav,audio/webm"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPickFile(f);
            // Reset value so picking the same file again still fires onChange.
            e.target.value = '';
          }}
        />
        <div ref={overflowWrapperRef} className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setOverflowOpen((v) => !v);
            }}
            aria-haspopup="menu"
            aria-expanded={overflowOpen}
            aria-label="פקודות נוספות"
            title="פקודות נוספות"
            className="flex h-full items-center justify-center rounded-lg border border-[var(--color-border)] px-3 font-mono text-base font-bold leading-none text-[var(--color-muted)] hover:text-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
          >
            /
          </button>
          {overflowOpen ? (
            <div
              role="menu"
              className="absolute bottom-full start-0 z-50 mb-2 w-[calc(100vw-1.5rem)] max-w-[720px] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] py-1 shadow-[var(--shadow-frame)]"
              style={{ animation: 'dsh-pop-in 150ms ease-out' }}
            >
              {SLASH_COMMANDS.map((cmd) => (
                  <button
                    key={cmd.command}
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setOverflowOpen(false);
                      onSlashSend(cmd.command);
                    }}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm text-[var(--color-text)] hover:bg-[var(--color-surface)]"
                  >
                    <span>{cmd.label}</span>
                    <span className="font-mono text-xs text-[var(--color-muted)]">
                      {cmd.command}
                    </span>
                  </button>
                ))}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingAttachment || !!attachment}
          aria-label="צרף קובץ"
          title="צרף קובץ"
          className="rounded-lg border border-[var(--color-border)] px-3 text-[var(--color-muted)] hover:text-[var(--color-primary)] disabled:opacity-30"
        >
          {/* Paperclip icon — heroicons outline, currentColor stroke. */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-5 w-5"
            aria-hidden="true"
          >
            <path d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 11-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
          </svg>
        </button>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_MESSAGE_LEN))}
          maxLength={MAX_MESSAGE_LEN}
          placeholder="שאלו משהו..."
          aria-label="הודעה"
          className="flex-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-start outline-none focus:border-[var(--color-primary)]"
          dir="rtl"
        />
        <button
          type="submit"
          disabled={!canSend}
          className="rounded-lg bg-[var(--color-primary)] px-4 text-sm font-bold text-white disabled:opacity-40"
        >
          שלחו
        </button>
      </div>
      {showCounter ? (
        <div
          className={`mx-auto mt-1 max-w-[720px] text-xs tabular-nums ${counterColor}`}
          aria-live="polite"
        >
          {/* 4.2 — isolate the numeric run: two LTR number groups
              with a neutral `/` separator reorder in RTL flow (would show
              `10000 / 9001`). dir="ltr" keeps it `9001 / 10000`. */}
          <span dir="ltr">
            {len} / {MAX_MESSAGE_LEN}
          </span>
        </div>
      ) : null}
    </form>
  );
};

// ---- Session-expiry re-auth (4.1) ---------------------------------

// Shown when an authenticated request (or the live stream) reports the session
// is no longer valid. Blocks the chat behind a modal and offers a single tap to
// re-authenticate. The unsent draft is already preserved in localStorage, so it
// rehydrates into the composer after the user logs back in.
const SessionExpiredOverlay = ({ onReauth }: { onReauth: () => void }) => (
  <div
    className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4"
    dir="rtl"
  >
    <div
      className="w-full max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 text-center shadow-[var(--shadow-frame)]"
      style={{ animation: 'dsh-pop-in 150ms ease-out' }}
      role="alertdialog"
      aria-modal="true"
    >
      <div className="text-lg font-bold text-[var(--color-text)]">
        ההתחברות פגה
      </div>
      <p className="mt-2 text-sm text-[var(--color-muted)]">
        יש להתחבר מחדש כדי להמשיך. מה שכתבתם נשמר.
      </p>
      <button
        type="button"
        onClick={onReauth}
        className="mt-4 w-full rounded-lg bg-[var(--color-primary)] py-2 text-sm font-bold text-white"
      >
        התחברות מחדש
      </button>
    </div>
  </div>
);

// ---- Impersonation -----------------------------------------------

// Persistent bar shown across the whole chat shell while this session is an
// impersonation session. The amber colour + 🎭 make it unmistakable; "return
// to self" is always one tap away.
const ImpersonationBanner = ({
  impersonating,
}: {
  impersonating: Impersonating;
}) => {
  const [ending, setEnding] = useState(false);
  const endImpersonation = async (): Promise<void> => {
    if (ending) return;
    setEnding(true);
    try {
      const res = await fetch('/admin/impersonate/end', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        // Signal sibling tabs, then full reload back as the admin.
        try {
          localStorage.setItem('dsh_impersonation_changed', String(Date.now()));
        } catch {
          /* storage disabled — ignore */
        }
        window.location.assign('/');
        return;
      }
    } catch {
      /* fall through to re-enable the button */
    }
    setEnding(false);
  };

  const target = impersonating.targetName ?? 'משתמש';
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-3 bg-[var(--color-warn)] px-4 py-2 text-sm text-white"
      role="status"
    >
      <span className="flex items-center gap-2">
        <span aria-hidden="true">🎭</span>
        <span>
          צופה כ־<strong className="font-bold">{target}</strong>
        </span>
      </span>
      <button
        type="button"
        onClick={() => void endImpersonation()}
        disabled={ending}
        className="rounded-md bg-white/20 px-3 py-1 text-xs font-bold hover:bg-white/30 disabled:opacity-50"
      >
        {ending ? '...' : 'חזרה לעצמי'}
      </button>
    </div>
  );
};

type AdminUser = {
  id: string;
  name: string | null;
  org_id: string | null;
  org_name: string | null;
  role_label: string | null;
  can_manage_platform: boolean;
  // (live-test pass 2): admin-on-admin block — picker disables
  // rows where either admin flag is true.
  can_impersonate: boolean;
  status: string;
};

// Modal user-picker. A platform admin picks a user; a successful pick
// reloads the SPA as that user. On `reauth_required` it routes to the
// step-up sub-flow (a single-use magic link delivered to the admin's
// Telegram — the genuine fresh re-auth).
const ImpersonationPicker = ({ onClose }: { onClose: () => void }) => {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [needStepup, setNeedStepup] = useState(false);
  const [stepupSent, setStepupSent] = useState(false);
  // (live-test pass) — the target the admin tapped before the
  // step-up prompt fired. Preserved so the code-paste success path can
  // auto-retry the same impersonate without forcing them to re-tap, AND
  // so the 2s auto-poll (path B: Telegram button click) knows whom to
  // mint as the moment the user-scoped stamp lands.
  const [pendingTargetId, setPendingTargetId] = useState<string | null>(null);
  // The 8-digit magic-code typed in by the admin from their Telegram message.
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let mounted = true;
    fetch('/admin/users', { credentials: 'include' })
      .then(async (res) => {
        if (!mounted) return;
        if (!res.ok) {
          setLoadError(true);
          return;
        }
        const data = (await res.json()) as { users: AdminUser[] };
        setUsers(Array.isArray(data.users) ? data.users : []);
      })
      .catch(() => {
        if (mounted) setLoadError(true);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const impersonate = async (id: string): Promise<void> => {
    if (busyId) return;
    setBusyId(id);
    setActionError(null);
    try {
      const res = await fetch('/admin/impersonate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ target_user_id: id }),
        credentials: 'include',
      });
      if (res.ok) {
        try {
          localStorage.setItem('dsh_impersonation_changed', String(Date.now()));
        } catch {
          /* storage disabled — ignore */
        }
        window.location.assign('/');
        return;
      }
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      if (res.status === 401 && body.error === 'reauth_required') {
        setPendingTargetId(id);
        setNeedStepup(true);
      } else {
        setActionError(body.message ?? 'הפעולה נכשלה');
      }
    } catch {
      setActionError('שגיאת רשת');
    } finally {
      setBusyId(null);
    }
  };

  const requestStepup = async (): Promise<void> => {
    setActionError(null);
    try {
      const res = await fetch('/admin/impersonate/request-stepup', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setStepupSent(true);
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      setActionError(body.message ?? 'שליחת הקישור נכשלה');
    } catch {
      setActionError('שגיאת רשת');
    }
  };

  // (live-test pass) — verify the typed code (path A). On 200,
  // immediately re-attempt the impersonate against the previously-picked
  // target so the admin doesn't have to tap them a second time.
  const verifyCode = async (): Promise<void> => {
    if (!isCompleteCode(code) || verifying) return;
    setVerifying(true);
    setActionError(null);
    try {
      const res = await fetch('/admin/impersonate/verify-stepup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
        credentials: 'include',
      });
      if (!res.ok) {
        setActionError('הקוד לא תקין או שפג תוקפו');
        setVerifying(false);
        return;
      }
      // Step-up stamped — chain straight into the impersonate mint.
      if (pendingTargetId) {
        await impersonate(pendingTargetId);
        // impersonate() reloads on success or surfaces an error itself.
      } else {
        // No pending target (shouldn't happen given UX flow, but defensive).
        setNeedStepup(false);
        setStepupSent(false);
        setCode('');
      }
    } catch {
      setActionError('שגיאת רשת');
    } finally {
      setVerifying(false);
    }
  };

  // (live-test pass) — auto-poll for path B: while we're in the
  // step-up state with a pending target, every 2s try to mint. If the admin
  // tapped the Telegram button on their phone, magic.ts stamps the user-
  // scoped step-up; the very next poll cycle's /admin/impersonate consumes
  // it and 200s, which reloads as the target. Polite cadence so the picker
  // never feels frozen but doesn't hammer the backend.
  useEffect(() => {
    if (!needStepup || !pendingTargetId || busyId !== null || verifying) return;
    const id = window.setInterval(() => {
      if (busyId !== null || verifying) return;
      // Fire-and-forget; impersonate() handles its own state.
      void impersonate(pendingTargetId);
    }, 2000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needStepup, pendingTargetId, busyId, verifying]);

  const q = query.trim().toLowerCase();
  const filtered = (users ?? []).filter(
    (u) =>
      !q ||
      (u.name ?? '').toLowerCase().includes(q) ||
      (u.org_name ?? '').toLowerCase().includes(q),
  );

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-[var(--shadow-frame)]"
        style={{ animation: 'dsh-pop-in 150ms ease-out' }}
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <h2 className="text-base font-bold text-[var(--color-text)]">
            החלפת זהות
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="סגור"
            className="rounded-full px-2 text-lg text-[var(--color-muted)] hover:text-[var(--color-bad)]"
          >
            ×
          </button>
        </div>

        {needStepup ? (
          <div className="flex flex-col gap-3 p-4 text-sm">
            {!stepupSent ? (
              <p className="text-[var(--color-text)]">
                לפני שמחליפים זהות צריך אישור. תלחצו "שלחו לי קוד" וקוד יישלח לערוץ שבו אתם רשומים (טלגרם או ווטסאפ).
              </p>
            ) : (
              <>
                <p className="text-[var(--color-text)]">
                  קוד אישור נשלח לערוץ שבו אתם רשומים (טלגרם או ווטסאפ). הקלידו אותו כאן:
                </p>
                <CodeInput
                  value={code}
                  onChange={setCode}
                  onSubmit={() => void verifyCode()}
                />
                <p className="text-xs text-[var(--color-muted)]">
                  פתחו את ההודעה ולחצו על הקישור — נמשיך כאן אוטומטית.
                </p>
              </>
            )}
            {actionError ? (
              <p className="text-xs text-[var(--color-bad)]" role="alert">
                {actionError}
              </p>
            ) : null}
            <div className="flex gap-2">
              {stepupSent ? (
                <button
                  type="button"
                  onClick={() => void verifyCode()}
                  disabled={!isCompleteCode(code) || verifying}
                  className="flex-1 rounded-lg bg-[var(--color-primary)] py-2 text-sm font-bold text-white disabled:opacity-40"
                >
                  {verifying ? '...' : 'אישור'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void requestStepup()}
                  className="flex-1 rounded-lg bg-[var(--color-primary)] py-2 text-sm font-bold text-white"
                >
                  שלחו לי קוד
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setNeedStepup(false);
                  setStepupSent(false);
                  setActionError(null);
                  setCode('');
                  setPendingTargetId(null);
                }}
                className="rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm"
              >
                חזרה
              </button>
            </div>
            {stepupSent ? (
              <button
                type="button"
                onClick={() => void requestStepup()}
                className="text-xs text-[var(--color-primary)] underline self-start"
              >
                שלחו שוב
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <div className="border-b border-[var(--color-border)] p-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="חיפוש לפי שם או ארגון..."
                aria-label="חיפוש משתמש"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm outline-none focus:border-[var(--color-primary)]"
                dir="rtl"
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loadError ? (
                <p className="p-4 text-center text-sm text-[var(--color-bad)]">
                  טעינת המשתמשים נכשלה
                </p>
              ) : users === null ? (
                <p className="p-4 text-center text-sm text-[var(--color-muted)]">
                  טוען...
                </p>
              ) : filtered.length === 0 ? (
                <p className="p-4 text-center text-sm text-[var(--color-muted)]">
                  לא נמצאו משתמשים
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {filtered.map((u) => {
                    // (live-test pass 2): admin-on-admin block —
                    // either admin flag disables the row.
                    const blocked =
                      u.can_manage_platform ||
                      u.can_impersonate ||
                      u.status !== 'active';
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          disabled={blocked || busyId !== null}
                          onClick={() => void impersonate(u.id)}
                          className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-start text-sm hover:bg-[var(--color-surface)] disabled:opacity-40"
                        >
                          <span className="flex flex-col">
                            <span className="font-medium text-[var(--color-text)]">
                              {u.name ?? '(ללא שם)'}
                            </span>
                            <span className="text-xs text-[var(--color-muted)]">
                              {[u.org_name, u.role_label]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </span>
                          </span>
                          {u.can_manage_platform || u.can_impersonate ? (
                            <span className="shrink-0 rounded bg-[var(--color-primary-light)] px-2 py-0.5 text-xs text-[var(--color-primary)]">
                              מנהל
                            </span>
                          ) : u.status !== 'active' ? (
                            <span className="shrink-0 rounded bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
                              {u.status}
                            </span>
                          ) : busyId === u.id ? (
                            <span className="shrink-0 text-xs text-[var(--color-muted)]">
                              ...
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {actionError ? (
              <p className="border-t border-[var(--color-border)] px-4 py-2 text-xs text-[var(--color-bad)]">
                {actionError}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
